import { Divider, Typography } from '@mui/material';
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Equipment, ISiteEquipment, PropertyState } from 'shared/api/api';
import { getTimeDateString } from 'shared/helpers/date-helpers';
import { localized } from 'shared/state/i18n/i18n';
import { GetDeviceConnectionString, GetDeviceEdgeAgentProperties } from 'shared/state/ducks/iot-hub/operations';
import { resetIotHubState } from 'shared/state/ducks/iot-hub/actions';
import { WebAppState } from 'web/state/store.web';
import { FlexColumn } from '../../basic-flex/flex-column';
import { FlexRow } from '../../basic-flex/flex-row';
import { SkioldButton } from '../../skiold-components/skiold-button/skiold-button';
import { FormTitle } from '../form-title';
import { Colors } from 'shared/assets/root-assets/colors';
import { useGetDeviceProxyProperties } from './use-get-device-proxy-properties';
import { EquipmentInfoSection } from './equipment-info-section';
import { EquipmentInfoLabelValue } from './equipment-info-label-value';
import { getIpAddressKey, getPropertyValue } from './equipment-information-helpers';

interface Props {
	equipment: ISiteEquipment;
}

export const EquipmentInformation: FC<Props> = memo(({ equipment }) => {
	const dispatch = useDispatch();
	const edgeModule = useGetDeviceProxyProperties(equipment);
	const edgeAgent = useSelector((state: WebAppState) => state.iotHub.edgeAgent);
	const connectionString = useSelector((state: WebAppState) => state.iotHub.connectionString);
	const iotHubError = useSelector((state: WebAppState) => state.iotHub.error);

	const [showConnectionString, setShowConnectionString] = useState(false);

	const ipAddressKey = useMemo(() => getIpAddressKey(equipment.equipment), [equipment.equipment]);

	useEffect(() => {
		if (!equipment.code) return;

		const code = equipment.code;
		const equipmentType = equipment.equipment;

		GetDeviceEdgeAgentProperties(code, equipmentType)(dispatch);
		GetDeviceConnectionString(code, equipmentType)(dispatch);
	}, [dispatch, equipment.code, equipment.equipment]);

	// Reset the shared IoT Hub slice when the panel closes/unmounts so re-opening never shows
	// the previously opened device's data (the modal unmounts this panel on close).
	useEffect(() => {
		return () => {
			dispatch(resetIotHubState());
		};
	}, [dispatch]);

	const propertyValue = useCallback(
		(name: string, state: PropertyState): string => getPropertyValue(edgeModule?.properties, name, state),
		[edgeModule],
	);

	const toggleConnectionStringVisibility = useCallback(() => setShowConnectionString(prev => !prev), []);

	const copyConnectionString = useCallback(() => {
		if (connectionString) {
			navigator.clipboard.writeText(connectionString);
		}
	}, [connectionString]);

	return (
		<FlexColumn className="form-section-bordered" style={{ backgroundColor: Colors.white }} sx={{ mt: 2 }}>
			<FormTitle title={localized('EquipmentInformation')} />
			{iotHubError && (
				<Typography sx={{ color: 'error.main', paddingX: 2, paddingTop: 1 }}>
					{localized('IotHubRequestFailed')}
				</Typography>
			)}
			<FlexColumn sx={{ padding: 2, gap: 2 }}>
				<EquipmentInfoSection title={localized('ConnectionString')}>
					<FlexRow sx={{ alignItems: 'center', gap: 1 }}>
						<Typography
							sx={{
								flex: 1,
								minWidth: 0,
								maxWidth: 480,
								fontFamily: 'monospace',
								wordBreak: 'break-all',
							}}
						>
							{showConnectionString
								? connectionString ?? '—'
								: '•'.repeat(connectionString?.length ?? 24)}
						</Typography>
						<SkioldButton
							title={showConnectionString ? localized('Hide') : localized('View')}
							onPress={toggleConnectionStringVisibility}
						/>
						<SkioldButton title={localized('Copy')} onPress={copyConnectionString} />
					</FlexRow>
				</EquipmentInfoSection>

				<Divider />

				<FlexRow sx={{ gap: 3 }}>
					<FlexColumn sx={{ flex: 1 }}>
						<EquipmentInfoSection title={localized('TimeZone')}>
							<EquipmentInfoLabelValue
								label={localized('Desired')}
								value={propertyValue('timeZone', PropertyState.Desired)}
							/>
							<EquipmentInfoLabelValue
								label={localized('Reported')}
								value={propertyValue('timeZone', PropertyState.Reported)}
							/>
						</EquipmentInfoSection>

						<EquipmentInfoSection title={localized('IpAddress')}>
							<EquipmentInfoLabelValue
								label={localized('Desired')}
								value={propertyValue(ipAddressKey, PropertyState.Desired)}
							/>
							<EquipmentInfoLabelValue
								label={localized('Reported')}
								value={propertyValue(ipAddressKey, PropertyState.Reported)}
							/>
						</EquipmentInfoSection>
					</FlexColumn>
					<FlexColumn sx={{ flex: 1 }}>
						<EquipmentInfoSection title={localized('LastUserUpdate')}>
							<EquipmentInfoLabelValue
								label={localized('Desired')}
								value={getTimeDateString(edgeModule?.desiredLastUpdate)}
							/>
							<EquipmentInfoLabelValue
								label={localized('Reported')}
								value={getTimeDateString(edgeModule?.reportedLastUpdate)}
							/>
						</EquipmentInfoSection>

						<EquipmentInfoSection title={localized('ModuleStatus')}>
							<EquipmentInfoLabelValue
								label={localized('RuntimeStatus')}
								value={edgeAgent?.runTimeStatus ?? '—'}
							/>
							<EquipmentInfoLabelValue
								label={localized('StatusDescription')}
								value={edgeAgent?.statusDescription ?? '—'}
							/>
							<EquipmentInfoLabelValue
								label={localized('ExitCode')}
								value={edgeAgent ? edgeAgent.exitCode.toString() : '—'}
							/>
							<EquipmentInfoLabelValue
								label={localized('LastStart')}
								value={getTimeDateString(edgeAgent?.lastStartTime)}
							/>
							<EquipmentInfoLabelValue
								label={localized('LastExit')}
								value={getTimeDateString(edgeAgent?.lastExitTime)}
							/>
							<EquipmentInfoLabelValue
								label={localized('RestartCount')}
								value={edgeAgent ? edgeAgent.restartCount.toString() : '—'}
							/>
						</EquipmentInfoSection>
					</FlexColumn>
				</FlexRow>
			</FlexColumn>
		</FlexColumn>
	);
});
